import { useState, useMemo, useId } from "react";
import { format } from "date-fns";
import { ArrowUpDown, ArrowUp, ArrowDown, MessageSquare, Eye } from "lucide-react";
import { cn } from "@/utils/cn";
import type { DraftCalendarItem } from "@/types/draftCalendar";

// ─── Sort logic ───────────────────────────────────────────────────────────────

type SortField =
  | "isRead"
  | "unSeenComments"
  | "showInGRC"
  | "signOffStatus"
  | "state"
  | "status"
  | "priorId"
  | "meetingId"
  | "business"
  | "meetingName"
  | "roomBlockStart"
  | "roomBlockEnd"
  | "eventStart"
  | "eventEnd"
  | "startTimeString"
  | "endTimeString"
  | "nightGuestNumber"
  | "allInAttendees"
  | "meetingType"
  | "businessLine"
  | "attendeeLevel"
  | "meetingCategory"
  | "summitProgramOwnerName"
  | "summitProgramContact"
  | "summitMeetingPlanner"
  | "programManager"
  | "summitMeetingStatus"
  | "duMeetingStatus";

type SortDir = "asc" | "desc";

function getSortValue(item: DraftCalendarItem, field: SortField): string | number {
  switch (field) {
    case "meetingId":
      if (item.meetingId) return `0_${item.meetingId}`;
      if (item.summitIntegrationStatus === "VALIDATION_ERROR" || item.summitIntegrationStatus === "SERVER_ERROR")
        return "1_error";
      if (item.summitIntegrationStatus === "PUSHED_TO_SUMMIT") return "2_pushed";
      return "3_none";
    case "summitMeetingStatus":
      return item.summitMeetingStatusSortOrder;
    case "duMeetingStatus":
      return item.duMeetingStatusSortOrder;
    case "isRead":
      return item.isRead ? 1 : 0;
    case "unSeenComments":
      return item.unSeenComments ? 1 : 0;
    case "showInGRC":
      return item.showInGRC ? 1 : 0;
    case "nightGuestNumber":
    case "allInAttendees":
      return item[field] ?? 0;
    case "eventStart":
    case "eventEnd":
    case "roomBlockStart":
    case "roomBlockEnd":
      return item[field] ?? "";
    default:
      return (item[field] as string | null) ?? "";
  }
}

function sortItems(items: DraftCalendarItem[], field: SortField, dir: SortDir) {
  return [...items].sort((a, b) => {
    const va = getSortValue(a, field);
    const vb = getSortValue(b, field);
    const cmp = typeof va === "number" && typeof vb === "number"
      ? va - vb
      : String(va).localeCompare(String(vb));
    return dir === "asc" ? cmp : -cmp;
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface DraftCalendarTableProps {
  items: DraftCalendarItem[];
  searchTerm: string;
  selected: Set<number>;
  onSelectionChange: (selected: Set<number>) => void;
  onRowClick: (item: DraftCalendarItem) => void;
  canEdit: boolean;
  canBulkAction: boolean;
  canToggleSettings?: boolean;
  signOffProcessStarted?: boolean;
  isAdmin?: boolean;
  isScheduler?: boolean;
}

function formatDate(d: string | null): string {
  if (!d) return "-";
  try { return format(new Date(d), "MM/dd/yyyy"); }
  catch { return d; }
}

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-slate-400" />;
  return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

// ─── Column definitions ───────────────────────────────────────────────────────

interface ColumnDef {
  key: SortField;
  label: string;
  className?: string;
  sortable?: boolean;
  visibility?: "always" | "admin-scheduler" | "toggle-settings" | "sign-off-visible";
}

const COLUMNS: ColumnDef[] = [
  { key: "isRead", label: "Read", className: "w-8", sortable: true, visibility: "always" },
  { key: "unSeenComments", label: "Comments", className: "w-12", sortable: true, visibility: "always" },
  { key: "showInGRC", label: "GRC", className: "w-8", sortable: true, visibility: "toggle-settings" },
  { key: "signOffStatus", label: "Sign Off Status", className: "min-w-[140px]", sortable: true, visibility: "sign-off-visible" },
  { key: "state", label: "Draft Status", className: "w-32", sortable: true, visibility: "always" },
  { key: "status", label: "Tier", className: "w-16", sortable: true, visibility: "toggle-settings" },
  { key: "priorId", label: "Prior ID", className: "w-20", sortable: true, visibility: "toggle-settings" },
  { key: "meetingId", label: "New ID", className: "w-20", sortable: true, visibility: "always" },
  { key: "business", label: "Business", className: "min-w-[120px]", sortable: true, visibility: "always" },
  { key: "meetingName", label: "Meeting Name", className: "min-w-[200px]", sortable: true, visibility: "always" },
  { key: "roomBlockStart", label: "Room Block Start", className: "w-32", sortable: true, visibility: "always" },
  { key: "roomBlockEnd", label: "Room Block End", className: "w-32", sortable: true, visibility: "always" },
  { key: "eventStart", label: "Meeting Start", className: "w-32", sortable: true, visibility: "always" },
  { key: "eventEnd", label: "Meeting End", className: "w-32", sortable: true, visibility: "always" },
  { key: "startTimeString", label: "Start Time", className: "w-24", sortable: false, visibility: "always" },
  { key: "endTimeString", label: "End Time", className: "w-24", sortable: false, visibility: "always" },
  { key: "nightGuestNumber", label: "Night Guests", className: "w-20", sortable: true, visibility: "always" },
  { key: "allInAttendees", label: "All In Attendees", className: "w-24", sortable: true, visibility: "always" },
  { key: "meetingType", label: "Meeting Type", className: "min-w-[130px]", sortable: true, visibility: "always" },
  { key: "businessLine", label: "Business Line", className: "min-w-[140px]", sortable: true, visibility: "always" },
  { key: "attendeeLevel", label: "Attendee Level", className: "min-w-[140px]", sortable: true, visibility: "always" },
  { key: "meetingCategory", label: "Meeting Category", className: "min-w-[140px]", sortable: true, visibility: "always" },
  { key: "summitProgramOwnerName", label: "CLO or Sponsoring PPMD", className: "min-w-[200px]", sortable: false, visibility: "always" },
  { key: "summitProgramContact", label: "Program Contact", className: "min-w-[150px]", sortable: false, visibility: "always" },
  { key: "summitMeetingPlanner", label: "Business Advisor", className: "min-w-[150px]", sortable: false, visibility: "always" },
  { key: "programManager", label: "Program Manager", className: "min-w-[150px]", sortable: false, visibility: "always" },
  { key: "summitMeetingStatus", label: "Summit Status", className: "min-w-[140px]", sortable: true, visibility: "toggle-settings" },
  { key: "duMeetingStatus", label: "DU Status", className: "min-w-[120px]", sortable: true, visibility: "toggle-settings" },
];

/**
 * Sortable, selectable Draft Calendar list table with full column parity to Blazor.
 */
export function DraftCalendarTable({
  items,
  searchTerm,
  selected,
  onSelectionChange,
  onRowClick,
  canEdit,
  canBulkAction,
  canToggleSettings = false,
  signOffProcessStarted = false,
  isAdmin = false,
  isScheduler = false
}: DraftCalendarTableProps) {
  const [sortField, setSortField] = useState<SortField>("eventStart");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const selectAllId = useId();

  // Filter columns based on visibility rules
  const visibleColumns = useMemo(() => {
    return COLUMNS.filter((col) => {
      switch (col.visibility) {
        case "toggle-settings":
          return canToggleSettings;
        case "sign-off-visible":
          return signOffProcessStarted || isAdmin || isScheduler;
        case "admin-scheduler":
          return isAdmin || isScheduler;
        case "always":
        default:
          return true;
      }
    });
  }, [canToggleSettings, signOffProcessStarted, isAdmin, isScheduler]);

  // Client-side filter by meeting name / business / ID
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return q
      ? items.filter(
          (i) =>
            i.meetingName?.toLowerCase().includes(q) ||
            i.business?.toLowerCase().includes(q) ||
            i.meetingId?.toLowerCase().includes(q)
        )
      : items;
  }, [items, searchTerm]);

  const sorted = useMemo(
    () => sortItems(filtered, sortField, sortDir),
    [filtered, sortField, sortDir]
  );

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function toggleAll() {
    if (selected.size === sorted.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(sorted.map((i) => i.id)));
    }
  }

  function toggleOne(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  const allSelected = sorted.length > 0 && selected.size === sorted.length;
  const someSelected = selected.size > 0 && selected.size < sorted.length;

  return (
    <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 180px)" }}>
      <table className="w-full border-collapse text-sm" aria-label="Draft calendar meetings">
        <thead className="sticky top-0 z-10 bg-[#E8E8E8]">
          <tr className="border-b border-slate-200">
            {/* Checkbox column */}
            {canBulkAction && (
              <th scope="col" className="w-10 px-3 py-2">
                <input
                  id={selectAllId}
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </th>
            )}

            {visibleColumns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn("px-3 py-2 text-left text-xs font-semibold text-black", col.className)}
                aria-sort={
                  sortField === col.key
                    ? sortDir === "asc" ? "ascending" : "descending"
                    : "none"
                }
              >
                {col.sortable !== false ? (
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className="inline-flex items-center gap-1 hover:text-slate-900"
                  >
                    {col.label}
                    <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1">{col.label}</span>
                )}
              </th>
            ))}

            <th scope="col" className="w-8 px-3 py-2" />
          </tr>
        </thead>

        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td
                colSpan={canBulkAction ? visibleColumns.length + 2 : visibleColumns.length + 1}
                className="py-10 text-center text-sm text-slate-500"
              >
                No meetings match your search.
              </td>
            </tr>
          ) : (
            sorted.map((item, idx) => (
              <tr
                key={item.id}
                className={cn(
                  "cursor-pointer border-b border-slate-100 hover:bg-slate-50",
                  idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]",
                  selected.has(item.id) && "ring-1 ring-inset ring-slate-400"
                )}
                onClick={() => onRowClick(item)}
              >
                {canBulkAction && (
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.meetingName ?? item.id}`}
                      checked={selected.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </td>
                )}

                {/* Render cells for visible columns */}
                {visibleColumns.map((col) => (
                  <td
                    key={`${item.id}-${col.key}`}
                    className={cn("px-3 py-2 text-slate-700", col.className)}
                    onClick={(e) => col.key === "showInGRC" && e.stopPropagation()}
                  >
                    <CellRenderer
                      item={item}
                      field={col.key}
                      canToggleSettings={canToggleSettings}
                    />
                  </td>
                ))}

                <td className="px-2 py-2 text-center" />
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Cell Renderer ──────────────────────────────────────────────────────────────

interface CellRendererProps {
  item: DraftCalendarItem;
  field: SortField;
  canToggleSettings: boolean;
}

function CellRenderer({ item, field, canToggleSettings }: CellRendererProps) {
  switch (field) {
    case "isRead":
      return item.isRead ? (
        <Eye className="h-4 w-4 text-slate-400" />
      ) : (
        <div className="h-2.5 w-2.5 rounded-full bg-blue-600" title="Unread" />
      );

    case "unSeenComments":
      if (!item.hasComments) return null;
      return (
        <div className="relative">
          <MessageSquare className="h-4 w-4 text-slate-600" />
          {item.unSeenComments && (
            <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
          )}
        </div>
      );

    case "showInGRC":
      if (!canToggleSettings) return null;
      return (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={item.showInGRC}
            readOnly
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-slate-200 peer-checked:bg-blue-600 rounded-full peer" />
          <span className="sr-only">Show in GRC: {item.showInGRC ? "Yes" : "No"}</span>
        </label>
      );

    case "signOffStatus":
      return (
        <div className="text-xs" title={item.signOffStatus ?? ""}>
          {item.signOffStatus ?? "—"}
        </div>
      );

    case "state":
      return (
        <StateBadge state={item.state ?? ""} />
      );

    case "status":
      // Tier: Core or Secondary
      return item.status ? (
        <span
          className={cn(
            "inline-block h-3 w-3 rounded-full",
            item.status.toLowerCase() === "core" ? "bg-blue-600" : "bg-gray-400"
          )}
          title={item.status}
        />
      ) : null;

    case "priorId":
      return <span className="text-xs">{item.priorId ?? "—"}</span>;

    case "meetingId":
      return (
        <MeetingIdCell
          meetingId={item.meetingId}
          status={item.state}
          integrationStatus={item.summitIntegrationStatus}
        />
      );

    case "business":
      return <span title={item.business ?? ""}>{item.business ?? "—"}</span>;

    case "meetingName":
      return (
        <div>
          <span className="block truncate font-medium text-slate-900" title={item.meetingName ?? ""}>
            {item.meetingName ?? "—"}
          </span>
          {!item.isRead && (
            <span className="inline-block rounded bg-blue-100 px-1 text-[10px] text-blue-700 mt-0.5">
              New
            </span>
          )}
          {item.unSeenComments && (
            <span className="inline-block rounded bg-amber-100 px-1 text-[10px] text-amber-700 ml-1 mt-0.5">
              New comments
            </span>
          )}
        </div>
      );

    case "roomBlockStart":
      return <span className="text-xs">{formatDate(item.roomBlockStart)}</span>;

    case "roomBlockEnd":
      return <span className="text-xs">{formatDate(item.roomBlockEnd)}</span>;

    case "eventStart":
      return <span className="text-xs">{formatDate(item.eventStart)}</span>;

    case "eventEnd":
      return <span className="text-xs">{formatDate(item.eventEnd)}</span>;

    case "startTimeString":
      return <span className="text-xs">{item.startTimeString || "—"}</span>;

    case "endTimeString":
      return <span className="text-xs">{item.endTimeString || "—"}</span>;

    case "nightGuestNumber":
      return (
        <span
          className="text-xs"
          title={item.nightGuestTooltip || `Night guests: ${item.nightGuestNumber}`}
        >
          {item.nightGuestNumber ?? "—"}
        </span>
      );

    case "allInAttendees":
      return <span className="text-xs">{item.allInAttendees ?? "—"}</span>;

    case "meetingType":
      return <span className="text-xs" title={item.meetingType ?? ""}>{item.meetingType ?? "—"}</span>;

    case "businessLine":
      return <span className="text-xs" title={item.businessLine ?? ""}>{item.businessLine ?? "—"}</span>;

    case "attendeeLevel":
      return <span className="text-xs" title={item.attendeeLevel ?? ""}>{item.attendeeLevel ?? "—"}</span>;

    case "meetingCategory":
      return <span className="text-xs" title={item.meetingCategory ?? ""}>{item.meetingCategory ?? "—"}</span>;

    case "summitProgramOwnerName":
      return <span className="text-xs" title={item.summitProgramOwnerName ?? ""}>{item.summitProgramOwnerName ?? "—"}</span>;

    case "summitProgramContact":
      return <span className="text-xs" title={item.summitProgramContact ?? ""}>{item.summitProgramContact ?? "—"}</span>;

    case "summitMeetingPlanner":
      return <span className="text-xs" title={item.summitMeetingPlanner ?? ""}>{item.summitMeetingPlanner ?? "—"}</span>;

    case "programManager":
      return <span className="text-xs" title={item.programManager ?? ""}>{item.programManager ?? "—"}</span>;

    case "summitMeetingStatus":
      return <span className="text-xs">{item.summitMeetingStatus ?? "—"}</span>;

    case "duMeetingStatus":
      return <span className="text-xs">{item.duMeetingStatus ?? "—"}</span>;

    default:
      return null;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

const STATE_COLORS: Record<string, string> = {
  Draft: "bg-slate-100 text-slate-700",
  "New Request": "bg-slate-100 text-slate-700",
  Submitted: "bg-blue-100 text-blue-700",
  Confirmed: "bg-green-100 text-green-800",
  Resolved: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-700",
  Declined: "bg-orange-100 text-orange-700",
  "DU Declined": "bg-orange-100 text-orange-700",
  Removed: "bg-red-100 text-red-700"
};

function StateBadge({ state }: { state: string }) {
  return (
    <span className={cn("rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap", STATE_COLORS[state] ?? "bg-slate-100 text-slate-600")}>
      {state}
    </span>
  );
}

function MeetingIdCell({
  meetingId,
  status,
  integrationStatus
}: {
  meetingId: string | null;
  status: string | null;
  integrationStatus: string | null;
}) {
  const hasId = !!meetingId;
  const showError =
    !hasId &&
    (integrationStatus === "VALIDATION_ERROR" ||
      integrationStatus === "SERVER_ERROR");
  const showPending =
    !hasId && status === "Resolved" && !integrationStatus;

  return (
    <div className="flex items-center justify-center gap-1 text-xs">
      {hasId && (
        <span className="font-mono text-blue-600">{meetingId}</span>
      )}
      {showError && (
        <div
          className="flex items-center justify-center h-4 w-4 rounded-full bg-red-100"
          title="Integration error"
        >
          <span className="text-red-700 text-xs">!</span>
        </div>
      )}
      {showPending && (
        <div
          className="flex items-center justify-center h-4 w-4 rounded-full bg-amber-100"
          title="Pending push to Summit"
        >
          <span className="text-amber-700 text-xs">⧗</span>
        </div>
      )}
      {!hasId && !showError && !showPending && (
        <span className="text-slate-300">—</span>
      )}
    </div>
  );
}
